import { Link } from 'react-router-dom';
import { CodeBracketIcon, DocumentTextIcon, StarIcon } from '@heroicons/react/24/outline';

function About() {
    return (
        <div className="bg-gray-50 min-h-screen py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-8 py-10 text-center border-b border-gray-100">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 tracking-tight">About UpTrend</h1>
                    <p className="text-gray-500 text-lg max-w-2xl mx-auto capitalize">
                        A robust, full-stack e-commerce platform built from scratch using the <strong>MERN stack</strong> to deliver a seamless and modern shopping experience.
                    </p>
                </div>

                <div className="px-8 py-10 space-y-12 text-gray-700">
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <CodeBracketIcon className="w-7 h-7 mr-2 text-indigo-600 stroke-2" />
                            Tech Stack
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm md:text-base">
                            <div className="space-y-3">
                                <p><strong className="text-gray-900">Frontend:</strong> React.js (CRA), Tailwind CSS, React Router</p>
                                <p><strong className="text-gray-900">State Management:</strong> Redux Toolkit (with Async Thunks)</p>
                                <p><strong className="text-gray-900">Backend:</strong> Node.js, Express.js</p>
                                <p><strong className="text-gray-900">Database:</strong> MongoDB (Mongoose)</p>
                            </div>
                            <div className="space-y-3">
                                <p><strong className="text-gray-900">Authentication:</strong> Google OAuth, JWT, Google reCAPTCHA</p>
                                <p><strong className="text-gray-900">Payments:</strong> Stripe Checkout</p>
                                <p><strong className="text-gray-900">Deployment:</strong> AWS EC2</p>
                            </div>
                        </div>
                    </section>

                    <hr className="border-gray-100" />

                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <DocumentTextIcon className="w-7 h-7 mr-2 text-indigo-600 stroke-2" />
                            Project Summary
                        </h2>
                        <div className="space-y-4 leading-relaxed text-gray-600">
                            <p>
                                UpTrend is a feature-rich, full-stack e-commerce web application developed from scratch using the MERN stack. While the user interface is heavily inspired by industry giants like Flipkart, UpTrend is <strong>not just a UI clone</strong>. It is backed by a fully functional, custom-built backend and a real database, delivering a complete, real-world user experience.
                            </p>
                            <p>
                                The frontend is architected using React.js and styled with Tailwind CSS for a highly responsive and modern look. Global state management, including cart and user authentication flows, is efficiently handled using Redux Toolkit with Async Thunks for seamless API integrations.
                            </p>
                            <p>
                                On the server side, the application runs on Node.js and Express.js, with MongoDB handling the complex data schemas for products, users, and orders. The platform prioritizes security with Google OAuth, JWT-based traditional logins, Google reCAPTCHA for spam prevention, and encrypted password hashing.
                            </p>
                            <p>
                                Users can browse an extensive product catalog, apply advanced filters, manage their cart and wishlist, and securely checkout using the Stripe payment gateway. The system utilizes synchronous return-URL redirection flows to verify payments and automatically update the order status upon a successful transaction.
                            </p>
                        </div>
                    </section>
                    <hr className="border-gray-100" />
                    <section>
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                            <StarIcon className="w-7 h-7 mr-2 text-indigo-600 stroke-2" />
                            Key Features
                        </h2>
                        <ul className="list-disc pl-5 space-y-3 text-gray-600 marker:text-indigo-600">
                            <li>Fully functional, responsive UI inspired by top e-commerce platforms.</li>
                            <li>Secure Authentication: Google Sign-In and JWT email/password with <strong>reCAPTCHA</strong> protection.</li>
                            <li>Advanced product browsing with dynamic filtering and search capabilities.</li>
                            <li>Global state management for Cart and Wishlist using <strong>Redux Toolkit</strong>.</li>
                            <li>Enterprise-grade payment processing via <strong>Stripe Checkout</strong> with secure success/cancel URL redirection.</li>
                            <li>Comprehensive user dashboard managing profiles, order history, and saved addresses.</li>
                            <li>Production-ready deployment hosted on <strong>AWS EC2</strong>.</li>
                        </ul>
                    </section>
                </div>

                <div className="bg-gray-50 px-8 py-6 text-center border-t border-gray-200 text-sm">
                    <Link to="/" className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
                        &larr; Return to Shopping
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default About;